import { useState, useEffect, useMemo } from 'react';
import ActionBar from '../actionBar';
import { Button, Badge } from 'react-bootstrap';
import Portal from '../portal';
import Table from '../table';
import BulkImportComponent from './bulkImport';
import { gql } from '@apollo/client';
import { useQuery } from "@apollo/client/react";
import { dateFormatter } from '../dateFormatter';
import {useAtomValue} from 'jotai';
import { pageSizeRecoil, showDeleted } from '../recoil';
import { usePortalContext } from '../../lib/context/portalContext';
import { useFeatureContext } from '../../lib/context/featureContext';
import { useCustomerContext } from '../../lib/context/customerContext';
import UserDetails from './userDetails';
import {useSearch, useSort} from '../../lib/useQueryModifiers';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMobile} from '@fortawesome/pro-light-svg-icons';

export const Users = props => {
  const {togglePortal, rowData, clicked} = usePortalContext();
  const [search, setSearch] = useSearch();
  const [sort, setSort] = useSort();
  const [pageNum, setPageNum] = useState(1);
  const [totalRecordsState, setTotalRecordsState] = useState(0);
  const showingDeleted = useAtomValue(showDeleted);
  const pageSize = useAtomValue(pageSizeRecoil);  
  const {feature_customer_mdm} = useFeatureContext();
  const {hasPermission} = useCustomerContext();
  
  const variables = useMemo(() => ({
    filters: {
      combineAnd: [
        ...search,
        ...sort,
        {
          field: "no_display",
          value: "false",
        },
        {
          field: "options.show_deleted",
          value: `${showingDeleted}`,
        },
        {
          field: "with.device", value: "1"
        },
        {
          field: "search.fields",
          value: `firstname`,
        },
        {
          field: "search.fields",
          value: `lastname`,
        },
        {
          field: "search.fields",
          value: `username`,
        },
        {
          field: "options.page.num",
          value: `${pageNum}`
        },
        {
          field: "options.page.len",
          value: `${pageSize}`
        }
      ]
    }
  }), [search, sort, showingDeleted, pageNum, pageSize]);
  
  const { data, previousData, loading } = useQuery(USERS, { variables });

  const totalQueried = (data ?? previousData)?.Users?.metadata?.page?.records ?? 0;
  let users = (data ?? previousData)?.Users?.result ?? [];
  users = users.map(user => { return { ...user, is_delete: showingDeleted }; });

  useEffect(() => {
    setPageNum(1)
  }, [search]);

  const columns = [
    {
      dataField: 'id',
      hidden: true
    },
    {
      dataField: 'firstname',
      text: 'First Name'
    },
    {
      dataField: 'lastname',
      text: 'Last Name'
    },
    {
      dataField: 'username',
      text: 'User ID'
    },
    {
      dataField: 'email',
      text: 'Email'
    },
    {
      dataField: 'phone',
      text: 'Phone'
    },
    {
      dataField: 'modified_at',
      text: 'Last Updated (UTC)',
      sort: true,
      formatter: dateFormatter
    }
  ];

  if(feature_customer_mdm) {
    const mdmColumn = {
      dataField: 'mdm_device_id',
      text: 'MDM',
      align: 'center',
      headerStyle: {textAlign: 'center'},
      formatter: (cell, row) => {
        if (cell) {
          return (
            <Badge pill data-testid="mdm-active-badge" className="mdm-active-badge" bg="primary">
              <FontAwesomeIcon className="me-1" icon={faMobile}/> Active
            </Badge>
          );
        }
        return null;
      }
    };
    columns?.splice(5, 0, mdmColumn )};

  return (
    <>
      <Portal title={clicked === 'importuser' ? "Bulk Import Users":"User"}>
        {(clicked === 'createuser') || (rowData?.user?.username) ?  <UserDetails type="user" title="User" /> : null }
        {clicked === 'importuser' ? <BulkImportComponent type="users" /> : null }
      </Portal>

      <ActionBar showHidden={false} searchChange={setSearch} title="Manage Users">
      <div>
        <Button data-testid="create-user-button" disabled={!hasPermission('USER_CREATE')} clicked={"createuser"} onClick={togglePortal}>Create User</Button>
        <Button data-testid="import-user-button" className="ms-3" disabled={!hasPermission('USER_BULK_CREATE')} defaultkey={0} clicked={"importuser"} onClick={togglePortal}>Import Users</Button>
      </div>
      </ActionBar>
      <Table
        testId="users-table"
        data={users}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        pageNum={pageNum}
        setPageNum={setPageNum}
        totalRecords={totalQueried}
        setSort={setSort}
      />
    </>
  );
};

const USERS = gql`
  query getUsers($filters: GenericFilters!) {
    Users (filters: $filters) {
      result {
        id
        username
        firstname
        lastname
        email
        phone
        is_active
        created_at
        modified_at
        mdm_device_id
      }
      metadata {
        page {
          num
          len
          total
          inPage
          records
        }
      }
    }
  }
`;

export default Users;
